import React from 'react';
import { useIntl } from 'react-intl';
import { gql, useQuery } from '@apollo/client';
import { 
  FileText as FileTextIcon, 
  Shield as ShieldCheckIcon, 
  AlertTriangle as AlertTriangleIcon,
  ArrowRight as ArrowRightIcon,
  CheckCircle2 as CheckCircle2Icon,
  Clock as ClockIcon,
  GraduationCap as GraduationCapIcon
} from 'lucide-react';
import Button from '../../../../ui/canon/Button';

// GraphQL query to fetch user's plan progress for all plan types
const GET_ALL_PLAN_STATUS = gql`
  query GetAllPlanStatus {
    getCurrentUser {
      id
      company {
        id
        features
      }
    }
    planTemplates {
      id
      name
      slug
      planType
      description
      ceCreditHours
      ceEligible
      planModules {
        id
        planSections {
          id
          planQuestions {
            id
          }
        }
      }
    }
    mySuccessionPlans {
      id
      status
      completionPercentage
      updatedAt
      completedAt
      ceCreditEarned
      userPlanResponses {
        id
      }
      planTemplate {
        id
        slug
        planType
      }
    }
  }
`;

// Plan configuration with icons and colors
const planConfig = {
  succession: {
    title: "Succession Plan",
    subtitle: "Deal readiness & transition strategy",
    description:
      "Create a practical strategy for continuity, emergencies, and deal readiness. Track your progress and take the next best step.",
    icon: FileTextIcon,
    color: "blue",
    emptyAction: "Create Succession Plan",
  },
  continuity: {
    title: "Continuity Plan",
    subtitle: "Business Continuity Plan (BCP)",
    description:
      "Protect your business from disruption. Set up your continuity plan to ensure operations continue smoothly.",
    icon: ShieldCheckIcon,
    color: "green",
    emptyAction: "Create Continuity Plan",
  },
  catastrophic: {
    title: "Catastrophic Plan",
    subtitle: "Emergency & Catastrophe Planning",
    description:
      "Prepare for the unexpected. Ensure your legacy survives catastrophic events with a comprehensive emergency plan.",
    icon: AlertTriangleIcon,
    color: "amber",
    emptyAction: "Create Catastrophic Plan",
  },
  // Map bcp to continuity for backwards compatibility
  bcp: {
    title: "Continuity Plan",
    subtitle: "Business Continuity Plan (BCP)",
    description:
      "Protect your business from disruption. Set up your continuity plan to ensure operations continue smoothly.",
    icon: ShieldCheckIcon,
    color: "green",
    emptyAction: "Create Continuity Plan",
  },
};

// Color classes for different plan types - using theme CSS variables
const colorClasses = {
  blue: {
    iconBg: "bg-succession-primary/10",
    iconColor: "text-succession-primary",
    progressStroke: "var(--succession-primary, #5489B6)",
  },
  green: {
    iconBg: "bg-succession-success/10",
    iconColor: "text-succession-success",
    progressStroke: "var(--succession-success, #10b981)",
  },
  amber: {
    iconBg: "bg-succession-warning/10",
    iconColor: "text-succession-warning",
    progressStroke: "var(--succession-warning, #f59e0b)",
  },
};

// Format relative time (e.g., "2 hours ago", "1 week ago")
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);

    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (_) {
    return null;
  }
};

/**
 * PlanStatusTile Component
 * 
 * @param {string} type - Plan type: "succession" | "continuity" | "catastrophic" | "bcp"
 * @param {string} templateName - Optional custom name from template (overrides default)
 * @param {string} templateDescription - Optional custom description from template (overrides default)
 * @param {string} status - Plan status: "empty" | "in_progress" | "completed"
 * @param {number} progress - Completion percentage (0-100)
 * @param {number} questionsAnswered - Number of questions answered
 * @param {number} totalQuestions - Total number of questions in the plan
 * @param {string} lastActivity - Last activity timestamp
 * @param {string} planSlug - Plan template slug for navigation
 * @param {object} ceCredit - CE credit info: { available, earned, creditHours }
 * @param {string} className - Additional CSS classes
 */
const PlanStatusTile = ({
  type,
  templateName,
  templateDescription,
  status = "empty",
  progress = 0,
  questionsAnswered = 0,
  totalQuestions = 0,
  lastActivity,
  planSlug,
  ceCredit = null,
  showHeaderIcon = true,
  className = "",
}) => {
  const intl = useIntl();
  // Get default config for this type, or use a generic fallback
  const defaultConfig = planConfig[type] || {
    title: templateName || "Plan",
    subtitle: "Planning Document",
    description: templateDescription || "Complete this plan to protect your business.",
    icon: FileTextIcon,
    color: "blue",
    emptyAction: `Create ${templateName || "Plan"}`,
  };
  
  // Override config with template-specific values if provided
  const config = {
    ...defaultConfig,
    title: templateName || defaultConfig.title,
    description: templateDescription || defaultConfig.description,
    emptyAction: templateName ? `Create ${templateName}` : defaultConfig.emptyAction,
  };
  
  const colors = colorClasses[config.color] || colorClasses.blue;
  const Icon = config.icon;

  // Derive the navigation URL
  // For completed plans, the plan page shows PlanCompletionCelebration with all export options
  const planUrl = planSlug ? `/succession/plans/${planSlug}` : "/succession/plans/new";

  return (
    <div className={`bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border p-6 hover:shadow-succession-lg transition-shadow flex flex-col h-full ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className={`flex items-start ${showHeaderIcon ? 'gap-4' : 'gap-0'}`}>
          {showHeaderIcon && (
            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${colors.iconBg}`}>
              <Icon className={`w-6 h-6 ${colors.iconColor}`} />
            </div>
          )}
          <div>
            <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>{config.title}</h3>
            <p className="text-sm text-succession-text-secondary leading-tight" style={{ margin: 0, padding: 0 }}>{config.subtitle}</p>
          </div>
        </div>
        {status === "completed" && (
          <div className="flex items-center gap-1.5 text-succession-success">
            <CheckCircle2Icon className="w-5 h-5" />
            <span className="text-sm font-medium">{intl.formatMessage({ id: 'succession.launchpad.plan_status_complete' })}</span>
          </div>
        )}
      </div>

      {/* Content - grows to fill available space */}
      <div className="flex-1">
        {status === "empty" && (
          <>
            <p className="text-sm text-succession-text-secondary leading-relaxed">
              {config.description}
            </p>
            
            {/* CE Credit Indicator for Empty State */}
            {ceCredit?.available && (
              <div className="flex items-center gap-2 text-xs text-succession-text-secondary bg-succession-bg-secondary rounded-md px-3 py-2 mt-3">
                <GraduationCapIcon className="w-3.5 h-3.5" />
                <span>
                  {intl.formatMessage({ id: 'succession.launchpad.plan_earn_credits_completion' }, { hours: ceCredit.creditHours, plural: ceCredit.creditHours > 1 ? 's' : '' })}
                </span>
              </div>
            )}
          </>
        )}

        {status === "in_progress" && (
          <>
            {/* Progress Circle */}
            <div className="flex items-center justify-center py-4">
              <div className="relative w-32 h-32">
                {/* Background circle */}
                <svg className="w-32 h-32 transform -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="var(--succession-border)"
                    strokeWidth="8"
                    fill="none"
                  />
                  {/* Progress circle */}
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke={colors.progressStroke}
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 56}`}
                    strokeDashoffset={`${2 * Math.PI * 56 * (1 - progress / 100)}`}
                    strokeLinecap="round"
                  />
                </svg>
                {/* Center text */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-succession-text-primary">{Math.round(progress)}%</div>
                    <div className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.launchpad.plan_status_complete_label' })}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-succession-text-secondary">
                  {intl.formatMessage({ id: 'succession.launchpad.plan_questions_answered' }, { answered: questionsAnswered, total: totalQuestions })}
                </span>
              </div>
              {lastActivity && (
                <div className="flex items-center gap-1.5 text-xs text-succession-text-muted">
                  <ClockIcon className="w-3 h-3" />
                  {intl.formatMessage({ id: 'succession.launchpad.plan_last_activity' }, { time: formatRelativeTime(lastActivity) })}
                </div>
              )}

              {/* CE Credit Indicator for In Progress State */}
              {ceCredit?.available && (
                <div className="flex items-center gap-2 text-xs text-succession-text-secondary bg-succession-bg-secondary rounded-md px-3 py-2">
                  <GraduationCapIcon className="w-3.5 h-3.5" />
                  <span>
                    {intl.formatMessage({ id: 'succession.launchpad.plan_earn_credits_completion' }, { hours: ceCredit.creditHours, plural: ceCredit.creditHours > 1 ? 's' : '' })}
                  </span>
                </div>
              )}
            </div>
          </>
        )}

        {status === "completed" && (
          <>
            <div className="bg-succession-bg-secondary rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-2xl font-bold text-succession-text-primary">{totalQuestions}</div>
                  <div className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.launchpad.plan_questions_label' })}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-succession-text-primary">100%</div>
                  <div className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.launchpad.plan_status_complete_label' })}</div>
                </div>
              </div>
              {lastActivity && (
                <div className="flex items-center gap-1.5 text-xs text-succession-text-muted pt-2 border-t border-succession-border">
                  <CheckCircle2Icon className="w-3 h-3" />
                  {intl.formatMessage({ id: 'succession.launchpad.plan_completed_at' }, { time: formatRelativeTime(lastActivity) })}
                </div>
              )}
            </div>
            
            {/* CE Credit Badge for Completed State */}
            {ceCredit?.available && (
              ceCredit.earned ? (
                // Earned state - static display
                <div className="rounded-lg p-3 border mt-3 bg-green-500/10 border-green-500/20 dark:bg-green-500/20 dark:border-green-500/30">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-green-500/20 dark:bg-green-500/30">
                      <GraduationCapIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-green-700 dark:text-green-300">
                          {intl.formatMessage({ id: 'succession.launchpad.plan_credit_earned_title' })}
                        </span>
                        <CheckCircle2Icon className="w-4 h-4 text-green-600 dark:text-green-400" />
                      </div>
                      <p className="text-xs text-succession-text-secondary mt-0.5">
                        {intl.formatMessage({ id: 'succession.launchpad.plan_credit_earned_detail' }, { hours: ceCredit.creditHours, plural: ceCredit.creditHours > 1 ? 's' : '' })}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                // Available state - clickable CTA
                <a
                  href={`${planUrl}?tab=ce-assessment`}
                  className="block rounded-lg p-3 border mt-3 bg-gradient-to-r from-succession-primary/10 to-succession-secondary/10 border-succession-primary/30 hover:border-succession-primary/50 hover:shadow-md transition-all cursor-pointer group"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-succession-primary/20 group-hover:bg-succession-primary/30 transition-colors">
                      <GraduationCapIcon className="w-5 h-5 text-succession-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-succession-primary">
                          {intl.formatMessage({ id: 'succession.launchpad.plan_earn_credits' }, { hours: ceCredit.creditHours, plural: ceCredit.creditHours > 1 ? 's' : '' })}
                        </span>
                        <ArrowRightIcon className="w-4 h-4 text-succession-primary group-hover:translate-x-1 transition-transform" />
                      </div>
                      <p className="text-xs text-succession-text-secondary mt-0.5">
                        {intl.formatMessage({ id: 'succession.launchpad.plan_ce_assessment' })}
                      </p>
                    </div>
                  </div>
                </a>
              )
            )}
          </>
        )}
      </div>

      {/* Footer - always at bottom */}
      <div className="mt-4 pt-4 border-t border-succession-border">
        {status === "empty" && (
          <Button
            as="a"
            href={planUrl}
            variant="outline"
            className="w-full group"
          >
            {config.emptyAction}
            <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        )}

        {status === "in_progress" && (
          <Button
            as="a"
            href={planUrl}
            variant="primary"
            className="w-full"
          >
            {intl.formatMessage({ id: 'succession.launchpad.plan_continue' })}
          </Button>
        )}

        {status === "completed" && (
          <div className="flex gap-2">
            <Button
              as="a"
              href={planUrl}
              variant="outline"
              className="flex-1"
            >
              {intl.formatMessage({ id: 'succession.launchpad.plan_view' })}
            </Button>
            <Button
              as="a"
              href={`${planUrl}?action=export`}
              variant="primary"
              className="w-full"
            >
              {intl.formatMessage({ id: 'succession.launchpad.plan_export_pdf' })}
            </Button>
            {type === 'catastrophic' && (
              <a
                href="/succession/pact-builder"
                className="flex items-center justify-center gap-1.5 w-full text-xs text-succession-text-secondary hover:text-succession-text-primary transition-colors py-1"
              >
                File Your Agreement
                <ArrowRightIcon className="w-3 h-3" />
              </a>
            )}
          </div>
        )}

        {status === "completed" && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Button
                as="a"
                href={planUrl}
                variant="outline"
                className="flex-1"
              >
                View Plan
              </Button>
              <Button
                as="a"
                href={`${planUrl}?action=export`}
                variant="primary"
                className="flex-1"
              >
                Export PDF
              </Button>
            </div>
            {type === 'catastrophic' && (
              <a
                href="/succession/pact-builder"
                className="flex items-center justify-center gap-1.5 w-full text-xs text-succession-text-secondary hover:text-succession-text-primary transition-colors py-1"
              >
                File Your Agreement
                <ArrowRightIcon className="w-3 h-3" />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * PlanStatusTilesGrid Component
 * 
 * This component fetches all plan templates and user progress,
 * then renders a PlanStatusTile for each available plan template.
 * 
 * @param {string} className - Additional CSS classes
 * @param {boolean} showHeaderIcon - Whether to show icons in tile headers (default: true)
 * @param {React.ReactNode} children - Additional elements to render in the grid (e.g., DealRoomMetricsCard)
 */
const PlanStatusTilesGrid = ({ className = "", showHeaderIcon = true, children }) => {
  const intl = useIntl();
  const { data, loading, error } = useQuery(GET_ALL_PLAN_STATUS, { fetchPolicy: 'cache-first' });

  if (loading) {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${className}`}>
        {[1, 2].map((i) => (
          <div 
            key={i} 
            className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border p-6 h-64 animate-pulse"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-succession-bg-secondary" />
              <div className="flex-1 space-y-2">
                <div className="h-5 bg-succession-bg-secondary rounded w-32" />
                <div className="h-4 bg-succession-bg-secondary rounded w-48" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-6 text-succession-error">
        {intl.formatMessage({ id: 'succession.launchpad.unable_to_load_plan_status' })}
      </div>
    );
  }

  const templates = data?.planTemplates || [];
  const userPlans = data?.mySuccessionPlans || [];
  const currentUser = data?.getCurrentUser;
  
  // Check if plan_builder_ce feature is enabled
  const features = currentUser?.company?.features || [];
  const ceFeatureEnabled = features.includes('plan_builder_ce');

  // Create a map of template ID -> user progress
  const progressByTemplateId = {};
  userPlans.forEach((plan) => {
    const templateId = plan.planTemplate?.id;
    if (templateId) {
      progressByTemplateId[templateId] = plan;
    }
  });

  // Helper to calculate total questions for a template
  const getTotalQuestions = (template) => {
    if (!template?.planModules) return 0;
    return template.planModules.reduce((modAcc, mod) => {
      return modAcc + (mod.planSections?.reduce((secAcc, sec) => {
        return secAcc + (sec.planQuestions?.length || 0);
      }, 0) || 0);
    }, 0);
  };

  // Helper to determine status
  const getStatus = (progress) => {
    if (!progress) return "empty";
    if (progress.status === "completed") return "completed";
    if (progress.status === "in_progress" || progress.status === "paused") return "in_progress";
    return "empty";
  };

  // Helper to map planType to display type (e.g., 'bcp' -> 'continuity')
  const getDisplayType = (planType) => {
    if (planType === 'bcp') return 'continuity';
    return planType;
  };

  // Build tiles from all available templates
  const tiles = templates.map((template) => {
    const progress = progressByTemplateId[template.id];
    const status = getStatus(progress);
    const totalQuestions = getTotalQuestions(template);
    const questionsAnswered = progress?.userPlanResponses?.length || 0;
    const completionPct = progress?.completionPercentage || 0;
    const lastActivity = progress?.updatedAt || progress?.completedAt;
    const displayType = getDisplayType(template.planType);
    
    // Build CE credit info if feature is enabled and template has CE
    let ceCredit = null;
    if (ceFeatureEnabled && template.ceEligible) {
      ceCredit = {
        available: true,
        earned: progress?.ceCreditEarned || false,
        creditHours: template.ceCreditHours || 1,
      };
    }

    return {
      templateId: template.id,
      type: displayType,
      templateName: template.name,
      templateDescription: template.description,
      status,
      progress: completionPct,
      questionsAnswered,
      totalQuestions,
      lastActivity,
      planSlug: template.slug,
      ceCredit,
    };
  });

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${className}`}>
      {tiles.map((tile) => (
        <PlanStatusTile
          key={tile.templateId}
          type={tile.type}
          templateName={tile.templateName}
          templateDescription={tile.templateDescription}
          status={tile.status}
          progress={tile.progress}
          questionsAnswered={tile.questionsAnswered}
          totalQuestions={tile.totalQuestions}
          lastActivity={tile.lastActivity}
          planSlug={tile.planSlug}
          ceCredit={tile.ceCredit}
          showHeaderIcon={showHeaderIcon}
        />
      ))}
      {children}
    </div>
  );
};

export { PlanStatusTile, PlanStatusTilesGrid };
export default PlanStatusTile;

